import type { RunRowLike } from "@/types/RunRow"

import {
  getGroupKeysForRow,
  TAG_UNTAGGED_KEY,
  type CompareBy,
} from "../../compare-by"

/** Header label for the multi-run compare column. */
export function getCompareColumnLabel(
  compareBy: CompareBy | null,
): string | null {
  if (!compareBy) {
    return null
  }
  if (compareBy.dimension === "agent") {
    return "Agent"
  }
  if (compareBy.dimension === "persona") {
    return "Persona"
  }
  if (compareBy.dimension === "test_case") {
    return "Test case"
  }
  if (compareBy.dimension === "tag") {
    return "Tag"
  }
  return compareBy.metadataKey ?? "Metadata"
}

/** Every group key this row falls under for the active compare dimension.
 *  Multi-value for dimension === "tag" (a row belongs to every one of its
 *  tags); a one-element list for the other dimensions. */
function getCompareValueKeys(
  row: RunRowLike,
  compareBy: CompareBy | null,
): string[] {
  if (!compareBy) {
    return []
  }
  return getGroupKeysForRow(row, compareBy)
}

/** Human-readable compare value for a row, resolving agent/persona display
 *  names. For dimension === "tag" a row can carry multiple tags — the
 *  column cell joins them with a comma so the row's full set is visible. */
export function getCompareDisplayValue(
  row: RunRowLike,
  compareBy: CompareBy | null,
  agentsById: Record<string, { display_name: string }> | null,
  personasById: Record<string, { display_name: string }> | null,
): string {
  const keys = getCompareValueKeys(row, compareBy)
  if (keys.length === 0) {
    return "—"
  }
  if (compareBy?.dimension === "agent") {
    return agentsById?.[keys[0]]?.display_name ?? keys[0]
  }
  if (compareBy?.dimension === "persona") {
    return personasById?.[keys[0]]?.display_name ?? keys[0]
  }
  if (compareBy?.dimension === "tag") {
    return keys
      .map((key) => (key === TAG_UNTAGGED_KEY ? "Untagged" : key))
      .join(", ")
  }
  return keys[0]
}

/** Assigned color for a row's compare group, if any. First key wins when
 *  the row belongs to multiple groups (tag dimension). */
export function getCompareColor(
  row: RunRowLike,
  compareBy: CompareBy | null,
): string | undefined {
  const [key] = getCompareValueKeys(row, compareBy)
  if (key == null) {
    return undefined
  }
  return compareBy?.colors?.[key]
}
